import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';

const MenuPageNew = () => {
  const { branch } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch branches
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'branches'));
        const branchList = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          branchList.push({
            id: doc.id,
            name: data.name,
            originalId: data.originalId || data.name
          });
        });

        if (branchList.length === 0) {
          // Default branches
          const defaultBranches = [
            { id: 'gewan', name: 'Gewan Branch', originalId: 'Branch 1' },
            { id: 'alwakrah', name: 'Al Wakrah Branch', originalId: 'Branch 2' }
          ];
          setBranches(defaultBranches);
          
          // Auto-select branch from URL
          if (branch) {
            const foundBranch = defaultBranches.find(b => b.id === branch);
            if (foundBranch) {
              setSelectedBranch(foundBranch);
            }
          } else {
            setSelectedBranch(null);
          }
        } else {
          setBranches(branchList);
          
          // Auto-select branch from URL
          if (branch) {
            const foundBranch = branchList.find(b => b.id === branch || b.originalId === branch);
            if (foundBranch) {
              setSelectedBranch(foundBranch);
            }
          } else {
            setSelectedBranch(null);
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching branches:', error);
        const defaultBranches = [
          { id: 'gewan', name: 'Gewan Branch', originalId: 'Branch 1' },
          { id: 'alwakrah', name: 'Al Wakrah Branch', originalId: 'Branch 2' }
        ];
        setBranches(defaultBranches);
        
        // Auto-select branch from URL
        if (branch) {
          const foundBranch = defaultBranches.find(b => b.id === branch);
          if (foundBranch) {
            setSelectedBranch(foundBranch);
          }
        } else {
          setSelectedBranch(null);
        }
        
        setLoading(false);
      }
    };

    fetchBranches();
  }, [branch]);

  // Fetch menu items for selected branch
  useEffect(() => {
    if (!selectedBranch) return;

    const fetchMenuItems = async () => {
      try {
        setLoading(true);
        console.log('Fetching menu items for branch:', selectedBranch);
        
        // Use the same approach as admin - query by branchId
        const menuRef = collection(db, 'menuItems');
        const q = query(menuRef, where('branchId', '==', selectedBranch.originalId));
        
        console.log('Querying with branchId:', selectedBranch.originalId);
        
        const querySnapshot = await getDocs(q);
        const items = [];
        const categorySet = new Set();

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          console.log('Found menu item:', data);
          items.push({ id: doc.id, ...data });
          if (data.category) categorySet.add(data.category);
        });

        console.log('Final items found:', items.length, items);
        setMenuItems(items.sort((a, b) => (a.order || 0) - (b.order || 0)));
        setCategories(['all', ...Array.from(categorySet)]);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching menu items:', error);
        setLoading(false);
      }
    };

    fetchMenuItems();
  }, [selectedBranch]);

  // Check if user is admin
  if (!loading && (!currentUser || !currentUser.isAdmin)) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'white',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Header />
        <main style={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '40px 20px'
        }}>
          <div style={{
            textAlign: 'center',
            maxWidth: '600px'
          }}>
            <h1 style={{
              fontSize: isMobile ? '48px' : '72px',
              fontWeight: '100',
              color: '#000',
              margin: '0 0 20px 0',
              letterSpacing: '4px'
            }}>
              404
            </h1>
            <h2 style={{
              fontSize: isMobile ? '18px' : '24px',
              fontWeight: '300',
              color: '#000',
              margin: '0 0 30px 0',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              lineHeight: '1.4'
            }}>
              SORRY, OOPSIE TECHNICAL ISSUES ARE HAPPENING RIGHT NOW, PLEASE COME BACK LATER.
            </h2>
            <button
              onClick={() => navigate('/')}
              style={{
                background: '#000',
                color: 'white',
                border: '2px solid #000',
                padding: isMobile ? '15px 30px' : '20px 40px',
                fontSize: isMobile ? '12px' : '14px',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Go Home
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Branch Selection Screen
  if (!selectedBranch) {
    return (
      <div style={{ minHeight: '100vh', background: '#fff' }}>
        <Header showLangToggle={true} showCart={false} />
        
        {loading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '80vh',
            fontSize: '18px',
            color: '#666'
          }}>
            Loading...
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            minHeight: '80vh',
            padding: isMobile ? '60px 20px' : '60px 40px',
            textAlign: 'center',
            background: '#ffffff'
          }}>
            {/* Decorative Line */}
            <div style={{
              width: '60px',
              height: '3px',
              background: 'linear-gradient(90deg, #000 0%, #666 100%)',
              marginBottom: '40px'
            }} />
            
            <h1 style={{
              fontSize: isMobile ? '42px' : '64px',
              fontWeight: '900',
              color: '#000',
              marginBottom: '24px',
              letterSpacing: '4px',
              textTransform: 'uppercase',
              lineHeight: '0.9',
              textShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}>
              Select Branch
            </h1>
            
            <p style={{
              fontSize: isMobile ? '14px' : '18px',
              color: '#888',
              marginBottom: '80px',
              textTransform: 'uppercase',
              letterSpacing: '6px',
              fontWeight: '400',
              opacity: '0.8'
            }}>
              Choose your location
            </p>

            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '24px' : '40px',
              maxWidth: isMobile ? '100%' : '800px',
              width: '100%'
            }}>
              {branches.map((branch) => (
                <div
                  key={branch.id}
                  style={{
                    flex: 1,
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <button
                    onClick={() => navigate(`/menu/${branch.id}`)}
                    style={{
                      width: '100%',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%)',
                      border: '1px solid #e0e0e0',
                      color: '#000',
                      fontSize: isMobile ? '16px' : '20px',
                      fontWeight: '700',
                      padding: isMobile ? '40px 20px' : '60px 32px',
                      cursor: 'pointer',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      textTransform: 'uppercase',
                      letterSpacing: '3px',
                      position: 'relative',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                      outline: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #000 0%, #333 100%)';
                      e.target.style.color = '#fff';
                      e.target.style.transform = 'translateY(-8px)';
                      e.target.style.boxShadow = '0 20px 60px rgba(0,0,0,0.25)';
                      e.target.style.borderColor = '#000';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%)';
                      e.target.style.color = '#000';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 8px 32px rgba(0,0,0,0.08)';
                      e.target.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '0',
                      left: '0',
                      right: '0',
                      height: '4px',
                      background: 'linear-gradient(90deg, #000 0%, #666 100%)',
                      opacity: '0',
                      transition: 'opacity 0.3s ease'
                    }} />
                    {branch.name}
                    <div style={{
                      position: 'absolute',
                      bottom: '16px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: '12px',
                      color: '#999',
                      fontWeight: '400',
                      letterSpacing: '2px'
                    }}>
                      VIEW MENU
                    </div>
                  </button>
                </div>
              ))}
            </div>
            
            {/* Decorative Elements */}
            <div style={{
              marginTop: '80px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              opacity: '0.3'
            }}>
              <div style={{ width: '20px', height: '1px', background: '#000' }} />
              <div style={{ width: '4px', height: '4px', background: '#000', borderRadius: '50%' }} />
              <div style={{ width: '20px', height: '1px', background: '#000' }} />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Filter and group items by category
  const filteredItems = menuItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         item.title_en?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Group items by category
  const groupedItems = filteredItems.reduce((acc, item) => {
    const category = item.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  // Sort categories and items within each category
  const sortedCategories = Object.keys(groupedItems).sort();
  const sortedGroupedItems = {};
  sortedCategories.forEach(category => {
    sortedGroupedItems[category] = groupedItems[category].sort((a, b) => (a.order || 0) - (b.order || 0));
  });

  // Menu Display
  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      <Header showLangToggle={true} showCart={false} />
      
      {/* Menu Header */}
      <div style={{
        textAlign: 'center',
        padding: isMobile ? '40px 20px' : '80px 40px',
        background: '#fff'
      }}>
        <h1 style={{
          fontSize: isMobile ? '32px' : '48px',
          fontWeight: '900',
          color: '#000',
          marginBottom: '16px',
          letterSpacing: '3px',
          textTransform: 'uppercase'
        }}>
          {selectedBranch.name}
        </h1>
        
        <p style={{
          fontSize: '16px',
          color: '#666',
          marginBottom: '40px',
          textTransform: 'uppercase',
          letterSpacing: '4px'
        }}>
          Menu
        </p>

        {/* Back to Branch Selection */}
        <button
          onClick={() => navigate('/menu')}
          style={{
            background: 'transparent',
            border: '1px solid #999',
            color: '#999',
            fontSize: '12px',
            fontWeight: '600',
            padding: '8px 16px',
            cursor: 'pointer',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '40px'
          }}
        >
          Change Branch
        </button>
      </div>

      {/* Search Bar */}
      <div style={{
        maxWidth: '600px',
        margin: '0 auto 40px',
        padding: '0 20px'
      }}>
        <input
          type="text"
          placeholder="Search menu..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '16px',
            border: 'none',
            borderBottom: '2px solid #eee',
            fontSize: '16px',
            background: 'transparent',
            outline: 'none'
          }}
        />
      </div>

      {/* Categories */}
      <div style={{
        display: 'flex',
        gap: '20px',
        padding: '20px 16px',
        overflowX: 'auto',
        justifyContent: isMobile ? 'flex-start' : 'center',
        marginBottom: '40px'
      }}>
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            style={{
              background: 'none',
              border: 'none',
              color: selectedCategory === category ? '#000' : '#999',
              fontSize: '14px',
              fontWeight: selectedCategory === category ? '700' : '500',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              whiteSpace: 'nowrap',
              padding: '8px 0',
              borderBottom: selectedCategory === category ? '2px solid #000' : 'none'
            }}
          >
            {category}
          </button>
        ))}
        {isMobile && <div style={{ minWidth: '20px' }} />}
      </div>

      {/* Menu Items */}
      {loading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
          fontSize: '16px',
          color: '#666'
        }}>
          Loading menu items...
        </div>
      ) : (
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px 40px'
        }}>
          {filteredItems.length === 0 ? (
            <div style={{
              textAlign: 'center',
              color: '#999',
              fontSize: '16px',
              padding: '60px 20px'
            }}>
              No items found
            </div>
          ) : (
            selectedCategory === 'all' ? (
              // Show items grouped by categories when 'all' is selected
              sortedCategories.map((category) => (
                <div key={category} style={{ marginBottom: '60px' }}>
                  <h2 style={{
                    fontSize: isMobile ? '24px' : '32px',
                    fontWeight: '900',
                    color: '#000',
                    marginBottom: '32px',
                    textTransform: 'uppercase',
                    letterSpacing: '2px'
                  }}>
                    {category}
                  </h2>
                  
                  {sortedGroupedItems[category].map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        padding: '24px 0',
                        borderBottom: '1px solid #f0f0f0',
                        gap: '20px'
                      }}
                    >
                      {item.imageUrl && (
                        <div style={{
                          width: isMobile ? '100px' : '120px',
                          height: isMobile ? '100px' : '120px',
                          flexShrink: 0
                        }}>
                          <img
                            src={item.imageUrl}
                            alt={item.name || item.title_en}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              background: '#f5f5f5',
                              border: '1px solid #e0e0e0'
                            }}
                          />
                        </div>
                      )}
                      
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{
                          fontSize: isMobile ? '16px' : '18px',
                          fontWeight: '700',
                          color: '#000',
                          margin: '0 0 8px 0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {item.name || item.title_en}
                        </h3>
                        
                        {item.description && (
                          <p style={{
                            fontSize: '14px',
                            color: '#666',
                            margin: '0 0 12px 0',
                            lineHeight: '1.5'
                          }}>
                            {item.description}
                          </p>
                        )}
                        
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '900',
                          color: '#000',
                          letterSpacing: '1px'
                        }}>
                          QAR {item.price || '0.00'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              // Show filtered items when a specific category is selected
              filteredItems.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: '24px 0',
                    borderBottom: '1px solid #f0f0f0',
                    gap: '20px'
                  }}
                >
                  {item.imageUrl && (
                    <div style={{
                      width: isMobile ? '100px' : '120px',
                      height: isMobile ? '100px' : '120px',
                      flexShrink: 0
                    }}>
                      <img
                        src={item.imageUrl}
                        alt={item.name || item.title_en}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          background: '#f5f5f5',
                          border: '1px solid #e0e0e0'
                        }}
                      />
                    </div>
                  )}
                  
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{
                      fontSize: isMobile ? '16px' : '18px',
                      fontWeight: '700',
                      color: '#000',
                      margin: '0 0 8px 0',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {item.name || item.title_en}
                    </h3>
                    
                    {item.description && (
                      <p style={{
                        fontSize: '14px',
                        color: '#666',
                        margin: '0 0 12px 0',
                        lineHeight: '1.5'
                      }}>
                        {item.description}
                      </p>
                    )}
                    
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '900',
                      color: '#000',
                      letterSpacing: '1px'
                    }}>
                      QAR {item.price || '0.00'}
                    </div>
                  </div>
                </div>
              ))
            )
          )}
        </div>
      )}
    </div>
  );
};

export default MenuPageNew;